import React from 'react';
import { Link } from 'react-router-dom';
import RevolvingGlobe from '../components/RevolvingGlobe';

const AboutUs: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-black to-deep-navy py-8 px-4 sm:px-6 lg:px-8">
      {/* Navigation Bar */}
      <nav className="max-w-[95%] xl:max-w-[85%] mx-auto flex justify-between items-center mb-12">
        <div className="text-xl font-bold text-accent-blue">Procista AI</div>
        <div className="flex space-x-6">
          <Link 
            to="/" 
            className="text-medium-gray hover:text-accent-blue transition-colors duration-300 font-medium"
          >
            Home
          </Link>
          <Link 
            to="/about" 
            className="text-medium-gray hover:text-accent-blue transition-colors duration-300 font-medium"
          >
            About Us
          </Link>
          <button 
            onClick={() => window.open('https://calendly.com/waceem/initial-project-consultation', '_blank')}
            className="bg-gradient-to-r from-navy-blue to-accent-blue hover:from-accent-blue hover:to-navy-blue text-white font-semibold py-2 px-6 rounded-full transition-all duration-300 text-sm"
          >
            Contact
          </button>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <header className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-accent-blue via-blue-400 to-navy-blue mb-6">
            About Procista AI
          </h1>
          <p className="text-xl text-light max-w-3xl mx-auto">
            Building intelligent solutions that make a measurable impact
          </p>
        </header>

        {/* Main Content */}
        <div className="space-y-12">
          {/* About Section */}
          <div className="bg-deep-navy rounded-2xl p-8 border border-navy-blue shadow-lg">
            <h2 className="text-3xl font-bold mb-6 text-accent-blue">Who We Are</h2>
            <p className="text-lg text-light leading-relaxed">
              At Procista AI, we specialize in building intelligent MVPs and scalable AI-driven solutions for forward-thinking businesses. Founded by Engr. Waseem Ahmed, who brings over a decade of experience in scaling businesses and building high-impact solutions, we focus on transforming ideas into impactful digital products through strategic guidance and collaborative innovation.
            </p>
          </div>

          {/* Mission & Approach */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-deep-navy rounded-2xl p-8 border border-navy-blue shadow-lg">
              <h2 className="text-2xl font-bold mb-4 text-accent-blue">Our Mission</h2>
              <p className="text-lg text-light">
                To deliver future-ready, intelligent systems that help organizations design, build, and launch solutions driving real business value.
              </p>
            </div>
            <div className="bg-deep-navy rounded-2xl p-8 border border-navy-blue shadow-lg">
              <h2 className="text-2xl font-bold mb-4 text-accent-blue">Our Approach</h2>
              <p className="text-lg text-light">
                We operate at the intersection of AI, SaaS, and advanced analytics, choosing the right technology stack for your specific needs.
              </p>
            </div>
          </div>

          {/* Process Section */}
          <div className="bg-deep-navy rounded-2xl p-8 border border-navy-blue shadow-lg">
            <h2 className="text-3xl font-bold mb-6 text-accent-blue">Our Process</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-navy-blue/30 p-6 rounded-xl border border-navy-blue text-center">
                <h3 className="text-xl font-bold mb-3 text-accent-blue">Discover & Design</h3>
                <p className="text-lg text-light">Understanding your vision and designing tailored solutions</p>
              </div>
              <div className="bg-navy-blue/30 p-6 rounded-xl border border-navy-blue text-center">
                <h3 className="text-xl font-bold mb-3 text-accent-blue">Build & Iterate</h3>
                <p className="text-lg text-light">Rapid development with continuous feedback loops</p>
              </div>
              <div className="bg-navy-blue/30 p-6 rounded-xl border border-navy-blue text-center">
                <h3 className="text-xl font-bold mb-3 text-accent-blue">Deliver & Support</h3>
                <p className="text-lg text-light">Production-ready solutions with ongoing support</p>
              </div>
            </div>
          </div>

          {/* Team Section */}
          <div className="bg-deep-navy rounded-2xl p-8 border border-navy-blue shadow-lg">
            <h2 className="text-3xl font-bold mb-6 text-accent-blue">Our Team</h2>
            <p className="text-lg text-light leading-relaxed mb-6">
              Our team blends strong technical expertise with real-world business experience. Alongside our founder, our team includes a member who is an alumnus of Imperial College London, bringing 12 years of industry experience, including work at Tamara, MOL, and other multinational companies.
            </p>
            <p className="text-lg text-light leading-relaxed">
              Supported by an IBM-certified team, we focus on rapid iteration, agile methodologies, and delivering high-quality solutions with efficiency and precision.
            </p>
          </div>


          {/* Contact Section */}
          <div className="bg-gradient-to-r from-accent-blue/10 to-navy-blue/20 rounded-2xl p-8 border border-accent-blue/30">
            <h2 className="text-3xl font-bold mb-6 text-center text-white">Get in Touch</h2>
            <p className="text-lg text-light text-center max-w-3xl mx-auto mb-8">
              Getting started is simple. Explore our demo solutions to understand our capabilities, then connect with us for a consultation. We'll align on your vision and tailor a solution that fits your goals.
            </p>
            <div className="text-center">
              <button 
                onClick={() => window.open('https://calendly.com/waceem/initial-project-consultation', '_blank')}
                className="bg-gradient-to-r from-navy-blue to-accent-blue hover:from-accent-blue hover:to-navy-blue text-white font-semibold py-4 px-10 rounded-full transition-all duration-300 transform hover:scale-105 text-lg"
              >
                Schedule Consultation
              </button>
            </div>
          </div>
        </div>

        {/* Footer CTA */}
        <section className="mt-24 py-12 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to transform your business?</h2>
          <p className="text-xl text-light max-w-3xl mx-auto mb-10">
            Book a free consultation with our experts to discover how our AI solutions can revolutionize your operations.
          </p>
          <button 
            onClick={() => window.open('https://calendly.com/waceem/initial-project-consultation', '_blank')}
            className="bg-gradient-to-r from-navy-blue to-accent-blue hover:from-accent-blue hover:to-navy-blue text-white font-semibold py-4 px-10 rounded-full transition-all duration-300 transform hover:scale-105 text-lg"
          >
            Schedule Free Demo
          </button>
        </section>
      </div>
      
      {/* Revolving Globe */}
      <RevolvingGlobe />
    </div>
  );
};

export default AboutUs;